import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon.js';

export interface MoreItem {
  icon: string;
  label: string;
  /** One line under the label saying what it does (or why it's off). */
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

/** The detail header's "More" menu (F10 P3): each item explains itself. */
export function MoreMenu({ items }: { items: MoreItem[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!items.length) return null;
  return (
    <div ref={ref} style={{ position: 'relative', flex: 'none' }}>
      <button className="btn sm" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        More <Icon name="chevron" size={13} />
      </button>
      {open && (
        <div
          role="menu"
          className="card"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 20,
            padding: 5,
            width: 290,
            boxShadow: 'var(--sh-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {items.map((it) => (
            <button
              key={it.label}
              role="menuitem"
              disabled={it.disabled}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 9,
                padding: '8px 10px',
                border: 'none',
                background: 'transparent',
                borderRadius: 6,
                cursor: it.disabled ? 'default' : 'pointer',
                textAlign: 'left',
                opacity: it.disabled ? 0.55 : 1,
                color: 'var(--text-1)',
              }}
              onMouseEnter={(e) => {
                if (!it.disabled) e.currentTarget.style.background = 'var(--bg-sunken)';
              }}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <Icon name={it.icon} size={15} style={{ marginTop: 2, flex: 'none', color: 'var(--text-2)' }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{it.label}</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>{it.description}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
